import React, { PureComponent } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { Modal, Text, TextInput, Checkbox, NumberInput, DateInput, i18n, tokens, localizationMaps, Services, TranslatedSentenceWithLink } from '@simplybenefits/sb-ui-library';
import FormControl from "@mui/material/FormControl";
import FormGroup from "@mui/material/FormGroup";
import FormControlLabel from "@mui/material/FormControlLabel";
import FormHelperText from "@mui/material/FormHelperText";

class MetaClaimInputModal extends PureComponent {
    state = {
        patient: this.initPatient(),
        category: this.props.meta ? this.props.meta.category : this.props.type === 'Lifestyle' ? (i18n.t(tokens.titles.lifestyleSpending)) : i18n.t(tokens.titles.paramedicals),
        service: this.props.meta ? this.props.meta.service : this.props.type === 'Lifestyle' ? (localizationMaps.fromEnglish('benefits', this.props.lsaClaimTypes?.[0]) || 'All') : i18n.t(tokens.dropdowns.benefits.acupuncturist),
        amountSubmitted: this.props.meta && !isNaN(parseInt(this.props.meta.amountSubmitted, 10)) ? `${ Number(this.props.meta.amountSubmitted).toFixed(2) }` : '',
        serviceDate: (this.props.meta && this.props.meta.serviceDate) ? Services.dates.formatIncomingDate(this.props.meta.serviceDate) : null,
        serviceOptions: this.props.type === 'Lifestyle' ? (this.props.lsaClaimTypes || []) : this.props.hsaClaimTypes.Paramedicals.reverse(),
        patientOptions: this.props.user.isDependent
            ? [{ name: `${ this.props.user.firstname } ${ this.props.user.lastname } (${ localizationMaps.fromEnglish("relationships", this.props.user.relationship) })`, id: this.props.user.dependentID }]
            : [{ name: `${ this.props.user.firstname } ${ this.props.user.lastname }`, id: this.props.user.userID }],
        acceptedHSATerms: false,
        acceptedHSATermsError: false
    };

    initPatient() {
        if (this.props.meta) {
            const patient = this.props.meta.patient;
            if (patient && 'name' in patient && 'id' in patient) {
                return patient;
            }

            return {
                name: this.props.meta.patient,
                id: this.props.meta.userID
            };
        }

        if (this.props.user.isDependent) {
            return {
                name: `${ this.props.user.firstname } ${ this.props.user.lastname } (${ localizationMaps.fromEnglish("relationships", this.props.user.relationship) })`,
                id: this.props.user.dependentID
            };
        }

        return {
            name: `${ this.props.user.firstname } ${ this.props.user.lastname }`,
            id: this.props.user.userID
        };
    }

    /**
     * @returns {Promise<void>}
     */
    async componentDidMount() {
        await this._saveData();

        if (this.props.dependents && !this.props.user.isDependent) {
            const dependents = this.props.dependents.map((dependent) => ({
                name: `${ dependent.firstname } ${ dependent.lastname } (${ localizationMaps.fromEnglish("relationships", dependent.relationship) })`,
                id: dependent.dependentID
            }));

            this.setState({
                patientOptions: [ ...this.state.patientOptions, ...dependents ]
            });
        }
    }

    _selectPatient = async (event) => {
        await this.setState({
            patient: this.state.patientOptions.find((e) => e.name === event.target.value)
        });

        this._saveData();
    };

    _selectCategory = async (event) => {
        await this.setState({
            category: event.target.value
        });

        this._setServiceOptions(event.target.value);
        this._saveData();
    };

    _selectService = async (event) => {
        await this.setState({
            service: event.target.value
        });

        this._saveData();
    };

    _handleInputChange = async (type, value) => {
        await this.setState({
            [type]: value
        });

        this._saveData();
    };

    _handleTermsChange = () => {
        this.setState({
            acceptedHSATerms: !this.state.acceptedHSATerms
        });
    };


    _saveData = () => {
        this.props.saveData({
            ...this.props.meta,
            patient: this.state.patient,
            category: this.state.category,
            service: this.state.service,
            amountSubmitted: this.state.amountSubmitted,
            serviceDate: this.state.serviceDate
        }, 'meta');
    };

    // eslint-disable-next-line consistent-return
    _next = async () => {
        const amountSubmittedError = await Services.validateInput('checkEmpty', this.state.amountSubmitted);
        const serviceDateError = await Services.validateInput('date', this.state.serviceDate, i18n.locale);

        this.setState({
            amountSubmittedError,
            serviceDateError
        });

        if (amountSubmittedError || serviceDateError) {
            return null;
        }

        if (this.props.type === 'HealthSpending' && !this.state.acceptedHSATerms) {
            this.setState({
                acceptedHSATermsError: i18n.t(tokens.labels.claims.firstSubmitClaims)
            });

            return null;
        }

        await this._saveData();
        this.props.next();
        return null;
    };

    _setServiceOptions = (category) => {
        category = localizationMaps.toEnglish("categories", category);

        const options = this.props.type === 'Lifestyle' ? (this.props.lsaClaimTypes || []) : this.props.hsaClaimTypes[category].reverse();

        this.setState({
            serviceOptions: options,
            service: localizationMaps.fromEnglish("benefits", options[0])
        });
    };

    render() {
        return (
            <Modal
                testid="submit-claim-modal"
                aria-labelledby="Submit Claim"
                open={true}
                onClose={ this.props.onClose }
                title={this.props.title}
                onBack={ this.props.onBack }
                loader={ this.props.loader }
                primaryButton={{
                    label: i18n.t(tokens.buttons.next),
                    onClick: this._next
                }}
            >
                <Text medium margin="0">
                    { i18n.t(tokens.labels.claims.patient) }:
                </Text>

                <TextInput
                    label={ i18n.t(tokens.labels.claims.selectPatient) }
                    variant="outlined"
                    value={ this.state.patient.name }
                    onChange={ this._selectPatient }
                    select
                    required
                    fullWidth
                    selectOptions={ this.state.patientOptions.map((e) => e.name) }
                />

                {
                    this.props.type === 'Lifestyle' ?
                        <React.Fragment>
                            {
                                !this.state.serviceOptions?.length ?
                                    <Text medium align="center" margin="5px 0 0 0">
                                        { i18n.t(tokens.labels.claims.noLifestyleSpendingServicesDefined) }
                                    </Text>
                                    :
                                    <React.Fragment>
                                        <Text medium margin="5px 0 0 0">
                                            { i18n.t(tokens.labels.claims.service) }:
                                        </Text>
                                        <TextInput
                                            label={ i18n.t(tokens.labels.claims.selectService) }
                                            variant="outlined"
                                            value={this.state.service}
                                            onChange={this._selectService}
                                            select
                                            required
                                            fullWidth
                                            selectOptions={ this.state.serviceOptions.map((serviceOption) => localizationMaps.fromEnglish("benefits", serviceOption.trim())) }
                                        />
                                    </React.Fragment>
                            }
                        </React.Fragment>
                        :
                        <React.Fragment>
                            <Text medium margin="5px 0 0 0">
                                { i18n.t(tokens.labels.claims.category) }:
                            </Text>

                            <TextInput
                                label={ i18n.t(tokens.labels.claims.selectCategory) }
                                variant="outlined"
                                value={this.state.category}
                                onChange={this._selectCategory}
                                select
                                required
                                fullWidth
                                selectOptions={ Services.selectFieldOptions.categoryOptions() }
                            />

                            <Text medium margin="5px 0 0 0">
                                { i18n.t(tokens.labels.claims.service) }:
                            </Text>

                            <TextInput
                                label={ i18n.t(tokens.labels.claims.selectService) }
                                variant="outlined"
                                value={this.state.service}
                                onChange={this._selectService}
                                select
                                required
                                fullWidth
                                selectOptions={ this.state.serviceOptions.map((serviceOption) => localizationMaps.fromEnglish("benefits", serviceOption.trim())) }
                            />
                        </React.Fragment>
                }

                <Text medium margin="5px 0 -5px 0">
                    { i18n.t(tokens.labels.claims.serviceDate) }:
                </Text>

                <DateInput
                    label={ i18n.t(tokens.labels.claims.serviceDate) }
                    value={ this.state.serviceDate || ''}
                    onChange={(e) => this._handleInputChange('serviceDate', e.target.value)}
                    error={Boolean(this.state.serviceDateError)}
                    helperText={this.state.serviceDateError}
                    fullWidth
                    required
                    testid="dateinput"
                    variant={"outlined"}
                />

                <Text medium margin="5px 0 -5px 0">
                    { i18n.t(tokens.labels.claims.amountPaid) }:
                </Text>

                <NumberInput
                    prefix="$"
                    label={ i18n.t(tokens.labels.claims.amountPaid) }
                    value={ this.state.amountSubmitted }
                    onChange={(e) => this._handleInputChange('amountSubmitted', e.target.value)}
                    error={Boolean(this.state.amountSubmittedError)}
                    helperText={this.state.amountSubmittedError}
                    fullWidth
                    required
                    testid="amountsubmitted"
                    variant={"outlined"}
                />

                {
                    this.props.type === 'HealthSpending' &&
                            <FormControl error={ Boolean(this.state.acceptedHSATermsError) }>
                                <FormGroup
                                    row
                                    style={{ marginTop: 20 }}
                                >
                                    <FormControlLabel
                                        style={{ margin: 0 }}
                                        control={
                                            <Checkbox
                                                checked={ this.state.acceptedHSATerms }
                                                onChange={ this._handleTermsChange }
                                                value="acceptedHSATerms"
                                                color="secondary"
                                            />
                                        }
                                        label={ <Text avMedium>{ i18n.t(tokens.labels.claims.submitClaimAcknowledgment, { patient: this.state.patient.name }) }</Text>}
                                    />
                                </FormGroup>

                                {
                                    this.state.acceptedHSATermsError &&
                                        <FormHelperText>{ this.state.acceptedHSATermsError }</FormHelperText>
                                }
                            </FormControl>
                }

                <TranslatedSentenceWithLink
                    token={ tokens.labels.claims.claimAgreementCheckbox }
                    onClick={ this.props.openTerms }
                    linkTextColor={ this.props.whitelabel?.primaryColor }
                />
            </Modal>
        );
    }
}

MetaClaimInputModal.propTypes = {
    whitelabel: PropTypes.object,
    user: PropTypes.object,
    dependents: PropTypes.array,
    hsaClaimTypes: PropTypes.object,
    lsaClaimTypes: PropTypes.array
};

/**
 *
 * @param {*} state
 */
const mapStateToProps = (state) => ({
    loader: state.loader,
    user: state.user.meta,
    dependents: state.user.dependents,
    hsaClaimTypes: state.usage.data.claimTypes,
    lsaClaimTypes: state.spendingAccounts.lsaClaimTypes,
    whitelabel: state.whitelabel
});

export default connect(mapStateToProps)(MetaClaimInputModal);